// PDF export for RigSentinel Calc calculation sheets using jsPDF
import jsPDF from "jspdf";
import { getDisplayUnit, fromImperialValue, convertLabeledMap } from "./metricUnits";
import { getCachedEntitlement } from "./entitlement";
import { isProActive, FeatureLockedError } from "./featureAccess";

// Phase A guard — PDF export requires an active RigSentinel Pro subscription.
// Throws FeatureLockedError if the cached entitlement does not grant Pro
// access. Callers should catch the error and surface an upgrade UI rather
// than letting it bubble as a generic crash.
function _requireProForExport() {
  if (!isProActive(getCachedEntitlement())) {
    throw new FeatureLockedError(
      "PDF export is available with RigSentinel Pro.",
      { feature: "pdf.export" },
    );
  }
}

// Resolve the display result/unit/inputs/extras for a saved entry, honoring
// the system the user was on at save time. Falls back to imperial.
function resolveDisplay(entry) {
  const system = entry.system || "imperial";
  const dispResult = entry.displayResult !== undefined && entry.displayResult !== null
    ? entry.displayResult
    : fromImperialValue(entry.result, entry.unit, system);
  const dispUnit = entry.displayUnit || getDisplayUnit(entry.unit, system);
  const dispInputs = convertLabeledMap(entry.inputs || {}, system);
  const dispExtras = entry.extras ? convertLabeledMap(entry.extras, system) : null;
  return { system, dispResult, dispUnit, dispInputs, dispExtras };
}

const COLORS = {
  amber: [255, 179, 0],
  dark: [10, 11, 14],
  bg: [22, 24, 29],
  text: [33, 37, 44],
  muted: [104, 113, 120],
  border: [200, 200, 200],
  danger: [198, 40, 40],
};

function drawHeader(doc, title, subtitle) {
  doc.setFillColor(...COLORS.dark);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 22, "F");
  doc.setFillColor(...COLORS.amber);
  doc.rect(0, 22, doc.internal.pageSize.getWidth(), 1.2, "F");

  doc.setTextColor(...COLORS.amber);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("RigSentinel", 12, 11);

  doc.setTextColor(220, 220, 220);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Calculation Sheet  ·  Field-Ready Calculations", 12, 17);

  doc.setTextColor(...COLORS.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(title, 12, 34);

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.muted);
    doc.text(subtitle, 12, 39);
  }
}

function drawSection(doc, y, label) {
  doc.setDrawColor(...COLORS.amber);
  doc.setLineWidth(0.6);
  doc.line(12, y, 18, y);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.amber);
  doc.text(label.toUpperCase(), 21, y + 1);
  return y + 5;
}

function wrap(doc, text, maxWidth) {
  return doc.splitTextToSize(String(text), maxWidth);
}

export function exportCalculationToPDF(entry) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const { system, dispResult, dispUnit, dispInputs, dispExtras } = resolveDisplay(entry);
  const subtitle = [
    new Date(entry.createdAt || Date.now()).toLocaleString(),
    entry.wellName ? `Well: ${entry.wellName}` : null,
    entry.rigName ? `Rig: ${entry.rigName}` : null,
    entry.stackMode ? `Stack: ${entry.stackMode === "subsea" ? "Subsea" : "Surface"}` : null,
    `Units: ${system.toUpperCase()}`,
  ].filter(Boolean).join("  ·  ");

  drawHeader(doc, entry.calcName || "Calculation", subtitle);

  let y = 48;

  // Result block
  doc.setFillColor(245, 245, 245);
  doc.rect(12, y, pageW - 24, 22, "F");
  doc.setDrawColor(...COLORS.amber);
  doc.setLineWidth(1.4);
  doc.line(12, y, 12, y + 22);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.muted);
  doc.text("FINAL ANSWER", 16, y + 5);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.text);
  doc.text(String(dispResult), 16, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.amber);
  doc.text(String(dispUnit || ""), 16 + doc.getTextWidth(String(dispResult)) + 4, y + 14);
  y += 28;

  // Formula
  y = drawSection(doc, y, "Formula");
  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.text);
  const flines = wrap(doc, entry.formula || "—", pageW - 28);
  doc.text(flines, 14, y);
  y += flines.length * 4.2 + 4;

  // Inputs
  y = drawSection(doc, y, "Inputs");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  Object.entries(dispInputs || {}).forEach(([k, v]) => {
    doc.setTextColor(...COLORS.muted);
    doc.text(String(k), 14, y);
    doc.setFont("courier", "normal");
    doc.setTextColor(...COLORS.text);
    doc.text(String(v), pageW - 14, y, { align: "right" });
    doc.setFont("helvetica", "normal");
    y += 4.4;
  });
  y += 2;

  // Steps
  y = drawSection(doc, y, "Step-by-step");
  if (system === "metric") {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    doc.text("Computed in imperial base units (ppg, psi, bbl, ft); display converted to metric.", 14, y);
    y += 4;
  }
  doc.setFont("courier", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLORS.text);
  (entry.steps || []).forEach((s, i) => {
    const num = String(i + 1).padStart(2, "0");
    const lines = wrap(doc, `${num}  ${s}`, pageW - 28);
    if (y > 270) { doc.addPage(); y = 20; }
    doc.text(lines, 14, y);
    y += lines.length * 4 + 1;
  });
  y += 3;

  // Extras
  if (dispExtras && Object.keys(dispExtras).length > 0) {
    y = drawSection(doc, y, "Additional");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    Object.entries(dispExtras).forEach(([k, v]) => {
      doc.setTextColor(...COLORS.muted);
      doc.text(String(k), 14, y);
      doc.setFont("courier", "normal");
      doc.setTextColor(...COLORS.text);
      doc.text(String(v), pageW - 14, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      y += 4.4;
    });
    y += 3;
  }

  if (entry.note) {
    y = drawSection(doc, y, "Note");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.text);
    const nlines = wrap(doc, entry.note, pageW - 28);
    doc.text(nlines, 14, y);
    y += nlines.length * 4.4 + 4;
  }

  // Safety footer
  if (y > 250) { doc.addPage(); y = 20; }
  doc.setDrawColor(...COLORS.danger);
  doc.setLineWidth(0.4);
  doc.rect(12, y, pageW - 24, 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.danger);
  doc.text("SAFETY", 14, y + 4);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.text);
  const safety =
    "Verify all inputs, units, formulas, and results before operational use. RigSentinel does not replace engineering judgment, company procedures, certified personnel, or regulatory requirements.";
  const sl = wrap(doc, safety, pageW - 28);
  doc.text(sl, 14, y + 9);

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.muted);
  doc.text("Generated by RigSentinel", 12, 290);
  doc.text(new Date().toLocaleString(), pageW - 12, 290, { align: "right" });

  const safe = (entry.calcName || "calculation").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  doc.save(`rigsentinel-${safe}-${Date.now()}.pdf`);
}

// =============================================================================
// DAILY SHEET — Bundle multiple calculations into a single branded PDF report
// =============================================================================

const CATEGORY_LABELS = {
  drilling: "Drilling",
  wellcontrol: "Well Control",
  trip: "Trip",
  mud: "Mud & Fluids",
  hydraulics: "Hydraulics",
  cement: "Cementing",
  casing: "Casing & Tubing",
  directional: "Directional",
  workover: "Workover & Completion",
  production: "Production",
  converters: "Conversions",
};

function drawDailyHeader(doc, pageNum, totalPages) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(...COLORS.dark);
  doc.rect(0, 0, pageW, 14, "F");
  doc.setFillColor(...COLORS.amber);
  doc.rect(0, 14, pageW, 0.8, "F");
  doc.setTextColor(...COLORS.amber);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("RigSentinel", 10, 9);
  doc.setTextColor(220, 220, 220);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("DAILY OPERATIONS SHEET", 38, 9);
  doc.setTextColor(200, 200, 200);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageW - 10, 9, { align: "right" });
}

function drawDailyFooter(doc) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.2);
  doc.line(10, 285, pageW - 10, 285);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...COLORS.muted);
  doc.text(
    "For calculation support only. Verify all results before operational use. RigSentinel does not replace engineering judgment, company procedures, certified personnel, or regulatory requirements.",
    10,
    289,
    { maxWidth: pageW - 20 }
  );
  doc.setTextColor(...COLORS.muted);
  doc.text(`Generated ${new Date().toLocaleString()}`, pageW - 10, 295, { align: "right" });
}

function buildCategoryBreakdown(entries) {
  const counts = {};
  entries.forEach((e) => {
    const c = e.category || "other";
    counts[c] = (counts[c] || 0) + 1;
  });
  return counts;
}

function drawCover(doc, entries, meta) {
  const pageW = doc.internal.pageSize.getWidth();

  // Big amber bar
  doc.setFillColor(...COLORS.dark);
  doc.rect(0, 0, pageW, 60, "F");
  doc.setFillColor(...COLORS.amber);
  doc.rect(0, 60, pageW, 2, "F");

  // RS mark
  doc.setFillColor(...COLORS.amber);
  doc.roundedRect(10, 14, 16, 16, 1.5, 1.5, "F");
  doc.setTextColor(...COLORS.dark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("RS", 18, 24, { align: "center" });

  doc.setTextColor(...COLORS.amber);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("Daily Operations Sheet", 30, 23);
  doc.setTextColor(220, 220, 220);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("RigSentinel · Field-Ready Calculations", 30, 30);

  // Meta block
  let y = 78;
  doc.setTextColor(...COLORS.muted);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text("REPORT METADATA", 10, y);
  y += 5;
  doc.setDrawColor(...COLORS.amber);
  doc.setLineWidth(0.4);
  doc.line(10, y, pageW - 10, y);
  y += 5;

  const metaRows = [
    ["Generated", new Date().toLocaleString()],
    ["Well", meta.wellName || "All wells"],
    ["Rig", meta.rigName || "All rigs"],
    ["Date Range", meta.dateRangeLabel || "All time"],
    ["Total Calculations", String(entries.length)],
  ];
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  metaRows.forEach(([k, v]) => {
    doc.setTextColor(...COLORS.muted);
    doc.text(k, 10, y);
    doc.setFont("courier", "normal");
    doc.setTextColor(...COLORS.text);
    doc.text(String(v), 50, y);
    doc.setFont("helvetica", "normal");
    y += 6.2;
  });

  // Category breakdown
  y += 4;
  doc.setTextColor(...COLORS.muted);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text("CATEGORY BREAKDOWN", 10, y);
  y += 5;
  doc.setDrawColor(...COLORS.amber);
  doc.line(10, y, pageW - 10, y);
  y += 5;

  const counts = buildCategoryBreakdown(entries);
  const total = entries.length || 1;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  Object.entries(counts).forEach(([cat, n]) => {
    const label = CATEGORY_LABELS[cat] || cat;
    const pct = Math.round((n / total) * 100);
    doc.setTextColor(...COLORS.muted);
    doc.text(label, 10, y);
    // bar
    const barX = 60;
    const barMaxW = pageW - 90;
    const barW = (n / total) * barMaxW;
    doc.setFillColor(...COLORS.border);
    doc.rect(barX, y - 3, barMaxW, 3, "F");
    doc.setFillColor(...COLORS.amber);
    doc.rect(barX, y - 3, barW, 3, "F");
    doc.setFont("courier", "normal");
    doc.setTextColor(...COLORS.text);
    doc.text(`${n}  (${pct}%)`, pageW - 10, y, { align: "right" });
    doc.setFont("helvetica", "normal");
    y += 6.2;
  });

  // Safety block
  if (y > 240) y = 240;
  y = 250;
  doc.setDrawColor(...COLORS.danger);
  doc.setLineWidth(0.4);
  doc.rect(10, y, pageW - 20, 28);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.danger);
  doc.text("SAFETY", 12, y + 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.text);
  const safety =
    "This report is a calculation aid for trained personnel. It is not a live well-control or operational instruction system. Verify every input, unit, formula, and result with qualified personnel before acting in the field. Follow your company's procedures and regulatory requirements.";
  const sl = doc.splitTextToSize(safety, pageW - 28);
  doc.text(sl, 12, y + 10);
}

function drawEntryBlock(doc, entry, y) {
  const pageW = doc.internal.pageSize.getWidth();
  const blockMaxW = pageW - 20;
  const { system, dispResult, dispUnit, dispInputs } = resolveDisplay(entry);

  // Amber tick + name + timestamp
  doc.setFillColor(...COLORS.amber);
  doc.rect(10, y, 3, 14, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.text);
  const nameLines = doc.splitTextToSize(entry.calcName || "Calculation", blockMaxW - 60);
  doc.text(nameLines[0], 17, y + 5);
  if (nameLines.length > 1) doc.text(nameLines[1], 17, y + 9);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.muted);
  const cat = CATEGORY_LABELS[entry.category] || "";
  const ts = new Date(entry.createdAt || Date.now()).toLocaleString();
  const ctx = [cat, ts, entry.wellName, entry.rigName, entry.stackMode ? `Stack: ${entry.stackMode === "subsea" ? "Subsea" : "Surface"}` : null, `Units: ${system.toUpperCase()}`].filter(Boolean).join("  ·  ");
  doc.text(ctx, 17, y + 12);

  // Result on the right (converted)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...COLORS.text);
  const valStr = String(dispResult);
  const unitStr = String(dispUnit || "");
  doc.text(valStr, pageW - 10, y + 6, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.amber);
  doc.text(unitStr, pageW - 10, y + 11, { align: "right" });

  y += 16;

  // Formula
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.text);
  const fl = doc.splitTextToSize(`f: ${entry.formula || "—"}`, blockMaxW - 8);
  doc.text(fl, 17, y);
  y += fl.length * 3.6 + 1;

  // Inputs in one compact line (converted)
  const inputStr = Object.entries(dispInputs || {}).map(([k, v]) => `${k}=${v}`).join("  ·  ");
  if (inputStr) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    const il = doc.splitTextToSize(`in: ${inputStr}`, blockMaxW - 8);
    doc.text(il, 17, y);
    y += il.length * 3.6 + 1;
  }

  // Optional note
  if (entry.note) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.text);
    const nl = doc.splitTextToSize(`note: ${entry.note}`, blockMaxW - 8);
    doc.text(nl, 17, y);
    y += nl.length * 3.6 + 1;
  }

  // Divider
  y += 2;
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.15);
  doc.line(10, y, pageW - 10, y);
  y += 4;

  return y;
}

export function exportDailySheetToPDF(entries, meta = {}) {
  _requireProForExport();
  if (!entries || entries.length === 0) {
    throw new Error("No calculations to export");
  }
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageH = doc.internal.pageSize.getHeight();

  // Cover (page 1)
  drawCover(doc, entries, meta);

  // Sort by createdAt descending (most recent first), then group by category for the body
  const sorted = [...entries].sort((a, b) =>
    new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  // Group by category for readability
  const grouped = {};
  sorted.forEach((e) => {
    const cat = e.category || "other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(e);
  });

  // Start a new page for the body
  doc.addPage();
  let y = 22;

  const catOrder = Object.keys(CATEGORY_LABELS).filter((c) => grouped[c]);
  Object.keys(grouped).forEach((c) => { if (!catOrder.includes(c)) catOrder.push(c); });

  catOrder.forEach((cat) => {
    const list = grouped[cat];
    if (!list) return;
    // Category header
    if (y > pageH - 40) { doc.addPage(); y = 22; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.amber);
    doc.text((CATEGORY_LABELS[cat] || cat).toUpperCase(), 10, y);
    doc.setTextColor(...COLORS.muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`${list.length} item${list.length === 1 ? "" : "s"}`, doc.internal.pageSize.getWidth() - 10, y, { align: "right" });
    y += 3;
    doc.setDrawColor(...COLORS.amber);
    doc.setLineWidth(0.5);
    doc.line(10, y, doc.internal.pageSize.getWidth() - 10, y);
    y += 4;

    list.forEach((entry) => {
      // Estimate block height (max ~32mm with note, ~24 without)
      const estimated = 24 + (entry.note ? 6 : 0);
      if (y + estimated > pageH - 18) { doc.addPage(); y = 22; }
      y = drawEntryBlock(doc, entry, y);
    });
    y += 4;
  });

  // Add headers / footers across pages
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    if (i > 1) drawDailyHeader(doc, i, total);
    drawDailyFooter(doc);
  }

  const safeName = (meta.fileLabel || "daily-sheet").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  doc.save(`rigsentinel-${safeName}-${Date.now()}.pdf`);
}

